import { useState } from 'react';
import { Leaf, ShieldCheck, Globe, BarChart3, Scale, Users, Banknote, Zap, BookOpen, ArrowRight } from 'lucide-react';

const EnergyEfficiency = () => {
    const [activePestel, setActivePestel] = useState(0);

    const pestelData = [
        {
            id: 'P',
            label: 'Político',
            title: 'Políticas de Eficiencia',
            desc: 'Marco regulatorio robusto y Ley de Eficiencia Energética que impulsa la descarbonización.',
            color: 'bg-blue-600',
            icon: <Scale className="w-6 h-6" />
        },
        {
            id: 'E',
            label: 'Económico',
            title: 'Modelo de Venta de Energía',
            desc: 'Inversión en infraestructura que garantiza ahorros operativos desde el primer día.',
            color: 'bg-green-600',
            icon: <Banknote className="w-6 h-6" />
        },
        {
            id: 'S',
            label: 'Social',
            title: 'Ahorro para el Cliente',
            desc: 'Reducción directa de costos que mejora la competitividad y el bienestar social.',
            color: 'bg-indigo-600',
            icon: <Users className="w-6 h-6" />
        },
        {
            id: 'T',
            label: 'Tecnológico',
            title: 'Integración de Sistemas',
            desc: 'Hibridación avanzada entre sistemas tradicionales y nuevas tecnologías limpias.',
            color: 'bg-energy-main',
            icon: <Zap className="w-6 h-6" />
        },
        {
            id: 'E',
            label: 'Ambiental',
            title: 'Reducción de Emisiones',
            desc: 'Disminución drástica de la huella de carbono mediante la electrificación térmica.',
            color: 'bg-accent-main',
            icon: <Leaf className="w-6 h-6" />
        },
        {
            id: 'L',
            label: 'Legal',
            title: 'Contratos a Largo Plazo',
            desc: 'Acuerdos de servicio que aseguran la continuidad operativa y cumplimiento normativo.',
            color: 'bg-slate-600',
            icon: <BookOpen className="w-6 h-6" />
        },
    ];

    return (
        <section id="eficiencia" className="py-32 bg-slate-50 relative overflow-hidden">
            <div className="section-container">
                {/* Header Section */}
                <div className="grid lg:grid-cols-2 gap-20 items-center mb-40">
                    <div className="order-2 lg:order-1" data-aos="fade-right">
                        <div className="badge-premium mb-8 bg-energy-blue/10 text-energy-blue border-energy-blue/20">
                            <Globe className="w-4 h-4" />
                            <span>Contexto Global</span>
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-primary-dark mb-8 leading-[1.1] tracking-tighter">
                            Transición Energética <br />
                            <span className="text-accent underline decoration-accent/20 underline-offset-8 italic">El Futuro es Eléctrico</span>
                        </h2>
                        <p className="text-xl text-slate-600 leading-relaxed font-light mb-12">
                            Chile lidera la transición hacia una matriz limpia. En CIAC Group alineamos su infraestructura con el flujo global de capital, donde la inversión en renovables duplica hoy a la de combustibles fósiles.
                        </p>

                        <div className="grid sm:grid-cols-2 gap-8">
                            <div className="group cursor-default">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-2xl bg-white shadow-premium flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                        <Globe className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-bold text-primary-dark">Flujo de Capital</h3>
                                </div>
                                <p className="text-sm text-slate-500 leading-relaxed">
                                    Por cada <span className="font-bold text-slate-800">1 USD</span> en fósiles, se invierten <span className="font-extrabold text-energy-blue text-lg">2 USD</span> en energías limpias.
                                </p>
                            </div>
                            <div className="group cursor-default">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-2xl bg-white shadow-premium flex items-center justify-center text-accent group-hover:scale-110 transition-transform">
                                        <BarChart3 className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-bold text-primary-dark">Estrategia IEA</h3>
                                </div>
                                <p className="text-sm text-slate-500 leading-relaxed">
                                    La electrificación térmica es uno de los <span className="font-bold text-slate-800">9 pilares críticos</span> para el Net Zero industrial.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="relative order-1 lg:order-2" data-aos="fade-left">
                        <div className="relative rounded-[3rem] overflow-hidden shadow-premium aspect-[4/3] group">
                            <img
                                src={`${import.meta.env.BASE_URL}images/global_energy_transition_light.png`}
                                alt="Transición Energética Global"
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/40 to-transparent" />
                        </div>
                        {/* Floating visual elements */}
                        <div className="absolute -bottom-8 -left-8 glass-card p-6 rounded-3xl animate-float">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-accent flex items-center justify-center text-white font-bold">4.0</div>
                                <div>
                                    <div className="text-[10px] uppercase font-bold text-slate-400">Rendimiento Medio</div>
                                    <div className="font-bold text-primary">COP Industrial</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Interactive PESTEL Tabs */}
                <div className="mb-40">
                    <div className="text-center mb-20">
                        <h3 className="text-3xl md:text-5xl font-black text-primary-dark mb-6 tracking-tighter">Análisis Estratégico <span className="text-accent underline decoration-accent/10 underline-offset-8">PESTEL</span></h3>
                        <p className="text-slate-500 font-light max-w-2xl mx-auto">Nuestra propuesta de valor analizada desde todas las dimensiones críticas del ecosistema industrial.</p>
                    </div>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-12 gap-8 items-start">
                            {/* Tab Selectors */}
                            <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-4">
                                {pestelData.map((item, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setActivePestel(index)}
                                        className={`
                                            group flex flex-col lg:flex-row items-center gap-4 p-4 rounded-2xl transition-all duration-300
                                            ${activePestel === index
                                                ? 'bg-primary text-white shadow-premium scale-[1.02]'
                                                : 'bg-white text-slate-400 hover:bg-slate-100 hover:text-primary-dark'}
                                        `}
                                    >
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg
                                            ${activePestel === index ? 'bg-white/20' : 'bg-slate-100'}
                                        `}>
                                            {item.id}
                                        </div>
                                        <span className="font-bold text-sm uppercase tracking-widest">{item.label}</span>
                                        <ArrowRight className={`ml-auto hidden lg:block transition-transform ${activePestel === index ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0'}`} size={18} />
                                    </button>
                                ))}
                            </div>

                            {/* Tab Content */}
                            <div className="lg:col-span-8">
                                <div className="glass-card p-8 md:p-12 rounded-[3rem] min-h-[400px] flex flex-col justify-center relative overflow-hidden bg-white">
                                    <div className="absolute top-0 right-0 w-64 h-64 opacity-5 pointer-events-none">
                                        <div className={`w-full h-full rounded-full ${pestelData[activePestel].color} blur-3xl`} />
                                    </div>

                                    <div className="relative z-10 animate-fade-in">
                                        <div className={`inline-flex items-center justify-center p-4 rounded-2xl ${pestelData[activePestel].color} text-white mb-8 shadow-lg`}>
                                            {pestelData[activePestel].icon}
                                        </div>
                                        <h4 className="text-4xl font-black text-primary-dark mb-6 tracking-tighter">
                                            {pestelData[activePestel].title}
                                        </h4>
                                        <p className="text-xl text-slate-600 leading-relaxed font-light mb-10">
                                            {pestelData[activePestel].desc}
                                        </p>
                                        <div className="flex items-center gap-6">
                                            <div className="flex -space-x-2">
                                                {[1, 2, 3].map(i => (
                                                    <div key={i} className="w-10 h-10 rounded-full border-2 border-white bg-slate-200" />
                                                ))}
                                            </div>
                                            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                                                Analizado por nuestro comité de expertos
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Call Action - Modern Banner */}
                <div className="relative rounded-[3.5rem] overflow-hidden bg-primary-dark group">
                    <div className="absolute inset-0 bg-mesh-gradient opacity-40 group-hover:opacity-60 transition-opacity" />
                    <div className="relative z-10 p-12 lg:p-24 flex flex-col lg:flex-row items-center justify-between gap-12">
                        <div className="max-w-2xl">
                            <h3 className="text-3xl md:text-5xl font-black text-white mb-6 mr-10 leading-tight">
                                Transforme el <span className="text-accent underline decoration-accent/20 underline-offset-8">Gasto Térmico</span> en Inversión Estratégica
                            </h3>
                            <p className="text-white/60 text-lg font-light mb-8 max-w-xl">
                                Auditorías energéticas avanzadas e implementación llave en mano con retornos de inversión garantizados.
                            </p>
                            <div className="flex flex-wrap gap-8">
                                <div className="flex items-center gap-3">
                                    <ShieldCheck className="text-accent w-6 h-6" />
                                    <span className="text-white font-bold text-sm">Certificación SEC</span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Leaf className="text-energy-blue w-6 h-6" />
                                    <span className="text-white font-bold text-sm">Sello Sostenible</span>
                                </div>
                            </div>
                        </div>
                        <button className="btn-accent px-10 py-6 text-xl shadow-2xl hover:scale-105">
                            Obtener Diagnóstico
                            <ArrowRight />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EnergyEfficiency;

